// File: src/components/modal/MovimentsCreateModal.tsx

import CloseIcon from "@mui/icons-material/Close";
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Modal,
  TextField,
  Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import api from "../../services/api";

import {
  DateTimePicker,
  LocalizationProvider,
  renderTimeViewClock
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ptBR } from "date-fns/locale/pt-BR";
import { useUser } from "../../auth/UserContext";
import { makeToasterErrorHandler } from "../../common/http/makeToasterErrorHandler";
import {
  MovimentInterface,
  UserInterface,
  WorkOrderInterface
} from "../../common/interfaces";
import { usePageToast } from "../../hooks/usePageToast";
import { TypeMovimentStatus } from "../enum/movimentStatusEnum";
import { WorkOrderServiceType } from "../enum/workOrderServiceTypeEnum";
import { InputSection } from "../style/moviment.styled";
import PageToaster from "../toaster/PageToaster";

interface MovimentsCreateModalProps {
  open: boolean;
  onClose: () => void;
  moviments?: MovimentInterface | null;
}

const MovimentsCreateModal: React.FC<MovimentsCreateModalProps> = ({
  open,
  onClose,
  moviments
}) => {
  const { isUser } = useAuth();
  const { user: current_user } = useUser();

  // --- Estados do formulário ---
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  const [statusValue, setStatusValue] = useState<TypeMovimentStatus>(
    TypeMovimentStatus.NONE
  );
  const [clientId, setClientId] = useState<number | "">("");
  const [userId, setUserId] = useState<number | "">("");
  const [woId, setWoId] = useState<number | "">("");

  const [isUpdate, setIsUpdate] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [movimentStartDate, setMovimentStartDate] = useState<Date | null>(
    new Date()
  );
  const [movimentEndDate, setMovimentEndDate] = useState<Date | null>(
    new Date()
  );

  // tolls
  const [loadingWo, setLoadingWo] = useState(false);
  const [wo, setWo] = useState<WorkOrderInterface[]>([]);
  const [woOption, setWoOption] = useState<WorkOrderInterface | null>(null);
  const [inputWoValue, setInputWoValue] = useState("");
  const [woSearch, setWoSearch] = useState("");

  // User
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [users, setUsers] = useState<UserInterface[]>([]);
  const [usersOption, setUsersOption] = useState<UserInterface | null>(null);
  const [inputUsersValue, setInputUsersValue] = useState("");
  const [usersSearch, setUsersSearch] = useState("");

  const containerId = "toaster-moviment-create-modal";
  const { autoSuccess, autoError, autoInfo, autoWarn } = usePageToast(
    containerId,
    true
  );

  const handleHttpError = makeToasterErrorHandler(autoError);

  const clear = () => {
    setReason("");
    setDescription("");
    setStatusValue(TypeMovimentStatus.NONE);
    setClientId("");
    setUserId("");
    setWoId("");
    setMovimentStartDate(new Date());
    setMovimentEndDate(new Date());
  };

  useEffect(() => {
    const fetchClients = async () => {
      setLoadingUsers(true);
      try {
        const res = await api.get("/user", {
          params: {
            page: 1,
            limit: 8,
            user_name: usersSearch || undefined
          }
        });
        setUsers(res.data.items || []);
      } catch (err) {
        console.error("Erro ao buscar clientes:", err);
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchClients();
  }, [usersSearch]);

  useEffect(() => {
    const fetchWo = async () => {
      setLoadingWo(true);

      try {
        const res = await api.get("/work-order", {
          params: {
            page: 1,
            limit: 8,
            work_order_number: woSearch || undefined,
            service_type: WorkOrderServiceType.EXTERNAL
          }
        });

        setWo(res.data.items || []);
        setClientId(res.data.items[0]?.client?.client_id);
      } catch (err) {
        console.error("Erro ao buscar Ativos:", err);
      } finally {
        setLoadingWo(false);
      }
    };
    fetchWo();
  }, [woSearch]);

  // Preenche campos no modo edição
  useEffect(() => {
    if (moviments) {
      setIsUpdate(true);
      setReason(moviments.moviment_reason);
      setDescription(moviments.moviment_description);
      setStatusValue(moviments.moviment_status);
      setClientId(moviments.client_id);
    } else {
      setIsUpdate(false);
      setReason("");
      setDescription("");
      setStatusValue(TypeMovimentStatus.NONE);
      setClientId("");
    }
  }, [moviments]);

  const handleSubmit = async () => {
    setIsSubmitting(true);

    // monta payload seguindo CreateMovimentDto
    const payload: any = {
      work_order_id: woId,
      moviment_reason: reason,
      moviment_description: description,

      // user_id: current_user?.user_id,
      client_id: clientId
    };

    if (!isUser) {
      payload.user_id = userId;
    } else {
      payload.user_id = current_user?.user_id;
    }

    if (!isUpdate) {
      payload.start_date = movimentStartDate;
    } else {
      payload.end_date = movimentEndDate;
      payload.moviment_status = statusValue;
    }

    try {
      if (isUpdate && moviments) {
        await api.put(`/moviment/${moviments.moviment_id}`, payload);
        autoSuccess("Movimentação atualizada com sucesso!");
      } else {
        await api.post("/moviment", payload);
        autoSuccess("Movimentação criada com sucesso!");
      }

      clear();
      onClose();
    } catch (err: any) {
      handleHttpError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <PageToaster containerId={containerId} />
      <Modal open={open} onClose={onClose} aria-labelledby="modal-moviment">
        <Box
          sx={{
            position: "absolute" as const,
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            border: "1px solid var(--border-strong)",
            p: 4,
            borderRadius: 2,
            boxShadow: 24,
            width: "30%",
            maxHeight: "90vh",
            overflowY: "auto"
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">
              {isUpdate ? "Editar Movimentação" : "Nova Movimentação"}
            </Typography>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          <InputSection>
            {!isUpdate && (
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={ptBR}
              >
                <DateTimePicker
                  label="Data inicial"
                  value={movimentStartDate}
                  onChange={setMovimentStartDate}
                  format="dd/MM/yyyy HH:mm"
                  viewRenderers={{
                    hours: renderTimeViewClock,
                    minutes: renderTimeViewClock,
                    seconds: renderTimeViewClock
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      margin: "normal"
                    }
                  }}
                />
              </LocalizationProvider>
            )}

            <Autocomplete
              sx={{ marginTop: "10px" }}
              options={wo}
              getOptionLabel={(opt) => opt.order_number.toString()}
              loading={loadingWo}
              // selected option
              value={woOption}
              onChange={(_, newVal) => {
                setWoOption(newVal);
                setWoId(newVal ? newVal.work_order_id : "");
                // quando muda a opção, também refletimos no input
                setInputWoValue(newVal ? newVal.order_number.toString() : "");
              }}
              // controla o texto que aparece dentro do campo
              inputValue={inputWoValue}
              onInputChange={(_, newInput) => {
                setInputWoValue(newInput); // digitação / limpeza
                setWoSearch(newInput); // sua lógica de buscar clientes
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Ordem de Serviço"
                  InputLabelProps={{ shrink: !!inputWoValue }} // força o label a flutuar
                  fullWidth
                />
              )}
              disableClearable={false}
              fullWidth
            />
          </InputSection>

          <InputSection>
            {!isUser && (
              <Autocomplete
                sx={{ marginTop: "10px" }}
                options={users}
                getOptionLabel={(opt) => opt.user_name}
                loading={loadingUsers}
                // selected option
                value={usersOption}
                onChange={(_, newVal) => {
                  setUsersOption(newVal);
                  setUserId(newVal ? newVal.user_id : "");
                  // quando muda a opção, também refletimos no input
                  setInputUsersValue(newVal ? newVal.user_name : "");
                }}
                // controla o texto que aparece dentro do campo
                inputValue={inputUsersValue}
                onInputChange={(_, newInput) => {
                  setInputUsersValue(newInput); // digitação / limpeza
                  setUsersSearch(newInput); // sua lógica de buscar clientes
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Usuário"
                    InputLabelProps={{ shrink: !!inputUsersValue }} // força o label a flutuar
                    fullWidth
                  />
                )}
                disableClearable={false}
                fullWidth
              />
            )}
          </InputSection>

          {/* Ações */}
          <Box display="flex" justifyContent="flex-end" mt={3}>
            <Button onClick={onClose} color="secondary" sx={{ mr: 2 }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              // disabled={isSubmitting || isUser}
            >
              {isUpdate ? "Atualizar" : "Cadastrar"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default MovimentsCreateModal;
